import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
// Kritiek pad blijft eager (homepage + layout); de rest wordt lui geladen (code-splitting).
import { HomePage } from "./features/home/HomePage";
import { SiteLayout } from "./components/SiteLayout";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { RouteFallback } from "./components/RouteFallback";
import { Seo } from "./components/Seo";
import { soortSlug } from "./lib/catalog";
import { CATEGORY_LANDINGS } from "./features/overview/category-landing-configs";
import { BENG_VARIANTS } from "./features/benglas/benglas-page-data";
import { trackPageView } from "./lib/track";
import { captureClickId } from "./lib/click-id";

// Route-componenten lui laden → kleinere hoofdbundle, snellere eerste render + lagere TBT.
const ConfiguratorRoute = lazy(() => import("./features/configurator/ConfiguratorRoute").then((m) => ({ default: m.ConfiguratorRoute })));
const CategoryPage = lazy(() => import("./features/category/CategoryPage").then((m) => ({ default: m.CategoryPage })));
const GlassoortenPage = lazy(() => import("./features/glas/GlassoortenPage").then((m) => ({ default: m.GlassoortenPage })));
const ProductPage = lazy(() => import("./features/product/ProductPage").then((m) => ({ default: m.ProductPage })));
const SearchResultsPage = lazy(() => import("./features/search/SearchResultsPage").then((m) => ({ default: m.SearchResultsPage })));
const UValueCalculator = lazy(() => import("./features/tools/UValueCalculator").then((m) => ({ default: m.UValueCalculator })));
const ShowerRenderRoute = lazy(() => import("./features/shower/ShowerRenderRoute").then((m) => ({ default: m.ShowerRenderRoute })));
const GlassWeightCalculator = lazy(() => import("./features/tools/GlassWeightCalculator").then((m) => ({ default: m.GlassWeightCalculator })));
const GlasOverview = lazy(() => import("./features/overview/GlasOverview").then((m) => ({ default: m.GlasOverview })));
const IsolatieglasLanding = lazy(() => import("./features/overview/IsolatieglasLanding").then((m) => ({ default: m.IsolatieglasLanding })));
const IsolatieglasConfigurator = lazy(() => import("./features/isolatieglas/IsolatieglasConfigurator").then((m) => ({ default: m.IsolatieglasConfigurator })));
const TripleConfigurator = lazy(() => import("./features/isolatieglas/TripleConfigurator").then((m) => ({ default: m.TripleConfigurator })));
const ShowerConfigurator = lazy(() => import("./features/shower/ShowerConfigurator").then((m) => ({ default: m.ShowerConfigurator })));
const ShowerPresetsPage = lazy(() => import("./features/shower/ShowerPresetsPage").then((m) => ({ default: m.ShowerPresetsPage })));
const InstallatieglasLanding = lazy(() => import("./features/overview/InstallatieglasLanding").then((m) => ({ default: m.InstallatieglasLanding })));
const CategoryGlasLanding = lazy(() => import("./features/overview/CategoryGlasLanding").then((m) => ({ default: m.CategoryGlasLanding })));
const CartPage = lazy(() => import("./features/cart/CartPage").then((m) => ({ default: m.CartPage })));
const CheckoutPage = lazy(() => import("./features/checkout").then((m) => ({ default: m.CheckoutPage })));
const PaymentResultPage = lazy(() => import("./features/checkout").then((m) => ({ default: m.PaymentResultPage })));
const BetaalLinkPage = lazy(() => import("./features/checkout").then((m) => ({ default: m.BetaalLinkPage })));
const NieuwsbriefAfmelden = lazy(() => import("./features/legal/NieuwsbriefAfmelden").then((m) => ({ default: m.NieuwsbriefAfmelden })));
const HerroepingPage = lazy(() => import("./features/legal/HerroepingPage").then((m) => ({ default: m.HerroepingPage })));
const RetourPage = lazy(() => import("./features/returns/RetourPage").then((m) => ({ default: m.RetourPage })));
const KnowledgeArticlePage = lazy(() => import("./features/knowledge/KnowledgeArticlePage").then((m) => ({ default: m.KnowledgeArticlePage })));
const KnowledgeIndexPage = lazy(() => import("./features/knowledge/KnowledgeIndexPage").then((m) => ({ default: m.KnowledgeIndexPage })));
const AdminPage = lazy(() => import("./features/admin").then((m) => ({ default: m.AdminPage })));
const OverOns = lazy(() => import("./pages/OverOns").then((m) => ({ default: m.OverOns })));
const ReviewsPage = lazy(() => import("./features/reviews/ReviewsPage").then((m) => ({ default: m.ReviewsPage })));
const FAQ = lazy(() => import("./pages/FAQ").then((m) => ({ default: m.FAQ })));
const Levertijden = lazy(() => import("./pages/Levertijden").then((m) => ({ default: m.Levertijden })));
const Herroepingsrecht = lazy(() => import("./pages/Herroepingsrecht").then((m) => ({ default: m.Herroepingsrecht })));
const Garantie = lazy(() => import("./pages/Garantie").then((m) => ({ default: m.Garantie })));
const ApiGlasprijs = lazy(() => import("./pages/ApiGlasprijs").then((m) => ({ default: m.ApiGlasprijs })));
const AlgemeneVoorwaarden = lazy(() => import("./pages/AlgemeneVoorwaarden").then((m) => ({ default: m.AlgemeneVoorwaarden })));
const Privacybeleid = lazy(() => import("./pages/Privacybeleid").then((m) => ({ default: m.Privacybeleid })));
const Cookies = lazy(() => import("./pages/Cookies").then((m) => ({ default: m.Cookies })));
const Contact = lazy(() => import("./pages/Contact").then((m) => ({ default: m.Contact })));
const Zakelijk = lazy(() => import("./pages/Zakelijk").then((m) => ({ default: m.Zakelijk })));
const GlasLatenPlaatsen = lazy(() => import("./pages/GlasLatenPlaatsen").then((m) => ({ default: m.GlasLatenPlaatsen })));
const Acties = lazy(() => import("./pages/Acties").then((m) => ({ default: m.Acties })));
const NotFound = lazy(() => import("./pages/NotFound").then((m) => ({ default: m.NotFound })));
const BenglasPage = lazy(() => import("./features/benglas/BenglasPage").then((m) => ({ default: m.BenglasPage })));
const VacuumglasLanding = lazy(() => import("./features/benglas/VacuumglasLanding").then((m) => ({ default: m.VacuumglasLanding })));
const CityIndexPage = lazy(() => import("./features/local/CityIndexPage").then((m) => ({ default: m.CityIndexPage })));
const CityPage = lazy(() => import("./features/local/CityPage").then((m) => ({ default: m.CityPage })));
const LoginPage = lazy(() => import("./features/account").then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("./features/account").then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import("./features/account").then((m) => ({ default: m.ForgotPasswordPage })));
const AccountLayout = lazy(() => import("./features/account").then((m) => ({ default: m.AccountLayout })));
const AccountHome = lazy(() => import("./features/account").then((m) => ({ default: m.AccountHome })));
const AccountOrders = lazy(() => import("./features/account").then((m) => ({ default: m.AccountOrders })));
const AccountAddresses = lazy(() => import("./features/account").then((m) => ({ default: m.AccountAddresses })));
const AccountDetails = lazy(() => import("./features/account").then((m) => ({ default: m.AccountDetails })));
const AccountPreferences = lazy(() => import("./features/account").then((m) => ({ default: m.AccountPreferences })));
const AccountSaved = lazy(() => import("./features/account").then((m) => ({ default: m.AccountSaved })));

/** Home, or redirect a returning Mollie payment (`/?order=`) to the result page. */
function RootIndex() {
  const [params] = useSearchParams();
  // Een betaalde losse factuur komt terug met `?factuur=`; de order die de webhook ervan maakt
  // draagt hetzelfde id, dus die kan gewoon als order worden doorgegeven. Zonder deze regel
  // belandde een betalende klant op een kale homepage (04-11-2026).
  const order = params.get("order") ?? params.get("factuur");
  // Mollie stuurt de klant terug naar de homepage met `?order=` (+ `&nr=`, het volgnummer). Die
  // `nr` moet mee in de doorverwijzing, anders valt de bedankpagina alsnog terug op het interne id.
  const nr = params.get("nr");
  if (order) {
    const vraag = `order=${encodeURIComponent(order)}${nr ? `&nr=${encodeURIComponent(nr)}` : ""}`;
    return <Navigate to={`/betaling?${vraag}`} replace />;
  }
  return <HomePage />;
}

function GlasOverviewRoute() {
  const navigate = useNavigate();
  return <GlasOverview onPick={(_slug, subcategory) => navigate(`/glas-op-maat/${soortSlug({ subcategory })}`)} />;
}

/** Oude losse configurator-stap; de soortpagina ís nu de configurator. Redirect blijvend. */
function SamenstellenRedirect() {
  const { type } = useParams();
  return <Navigate to={`/glas-op-maat/${type}`} replace />;
}

function CartRoute() {
  const navigate = useNavigate();
  return (
    <>
      <Seo title="Winkelwagen" canonicalPath="/winkelwagen" noindex />
      <CartPage onBack={() => navigate("/glassoorten")} onCheckout={() => navigate("/afrekenen")} />
    </>
  );
}

function CheckoutRoute() {
  const navigate = useNavigate();
  return (
    <>
      <Seo title="Afrekenen" canonicalPath="/afrekenen" noindex />
      <CheckoutPage
        onBack={() => navigate("/winkelwagen")}
        onDone={(status, orderId) => navigate("/betaling", { state: { status, orderId } })}
      />
    </>
  );
}

function BetalingRoute() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const state = (location.state ?? null) as { status?: "paid" | "failed" | "open"; orderId?: string; orderNumber?: number } | null;
  const orderId = state?.orderId ?? params.get("order") ?? undefined;
  // Het volgnummer komt mee uit de Mollie-terugkeer (`&nr=`) of uit de stub-flow (state). Zonder
  // dit toonde de bedankpagina de eerste acht tekens van het interne id.
  const nrParam = Number(params.get("nr"));
  const orderNrUrl = state?.orderNumber ?? (Number.isFinite(nrParam) && nrParam > 0 ? nrParam : undefined);

  /**
   * De uitkomst van de betaling. Stond hier ooit als:
   *
   * ```ts
   * const status = state?.status ?? (params.get("order") ? "paid" : "open");
   * ```
   *
   * Dat was fout: Mollie stuurt de klant bij **elke** uitkomst terug naar dezelfde `redirectUrl`,
   * dus `?order=` betekent "hij komt terug van Mollie" en niet "hij heeft betaald". Iedere
   * afgebroken of verlopen betaling kreeg zo "Bedankt voor uw bestelling! Uw betaling is gelukt",
   * waarna de winkelwagen werd geleegd en er een Ads-conversie vuurde voor geld dat nooit kwam.
   * Gevonden 05-11-2026 bij een factuur van € 1.050,26. Nu vraagt de pagina het de server.
   *
   * De stub-flow (lokale dev zonder Mollie) draagt de status nog wel in router-state; die is daar
   * de enige waarheid en wordt dus niet overruled.
   */
  const uitState = state?.status;
  const [opgehaald, setOpgehaald] = useState<{ status: "paid" | "failed" | "open"; orderNr?: number } | undefined>();
  useEffect(() => {
    if (uitState || !orderId) return;
    let actief = true;
    void (async () => {
      const { fetchPaymentOutcome } = await import("./features/checkout/checkout-service");
      const uitkomst = await fetchPaymentOutcome(orderId);
      // Geen antwoord (functie onbereikbaar, Mollie plat) → de "niet voltooid"-kant. Nooit
      // terugvallen op "gelukt": een klant die onnodig opnieuw probeert is te herstellen, een
      // klant die denkt dat hij klaar is niet.
      if (actief) setOpgehaald(uitkomst ?? { status: "failed" });
    })();
    return () => {
      actief = false;
    };
  }, [uitState, orderId]);

  // `undefined` = we weten het nog niet; de pagina toont dan "we controleren uw betaling".
  // Zonder id valt er niets te controleren (iemand opent /betaling rechtstreeks).
  const status = uitState ?? (orderId ? opgehaald?.status : "open");
  const orderNr = orderNrUrl ?? opgehaald?.orderNr;
  return (
    <>
      <Seo title="Bestelling" canonicalPath="/betaling" noindex />
      <PaymentResultPage
        status={status}
        orderId={orderId}
        orderNr={orderNr}
        onHome={() => navigate("/")}
        onRetry={async () => {
          // Eerst verder betalen op dezelfde order; dat scheelt de klant het hele afrekenscherm
          // én voorkomt een tweede ordernummer. Lukt dat niet, dan de oude weg.
          // Lui geladen, net als de kassa zelf: deze code hoort niet in de eerste bundel.
          const { resumePayment } = await import("./features/checkout/checkout-service");
          const r = orderId ? await resumePayment(orderId) : {};
          if (r.url) window.location.assign(r.url);
          else navigate("/afrekenen");
        }}
      />
    </>
  );
}

function HerroepenRoute() {
  const navigate = useNavigate();
  return (
    <>
      <Seo
        title="Herroepen / ontbinden"
        description="Herroep een standaard glasartikel met onze digitale herroepingsknop. Maatwerk is wettelijk uitgesloten van het herroepingsrecht."
        canonicalPath="/herroepen"
      />
      <HerroepingPage onHome={() => navigate("/")} />
    </>
  );
}

function AfmeldenRoute() {
  return (
    <>
      <Seo
        title="Uitschrijven voor de nieuwsbrief"
        description="Schrijft u in één stap uit voor de nieuwsbrief van Glastotaal."
        canonicalPath="/nieuwsbrief/afmelden"
        noindex
      />
      <NieuwsbriefAfmelden />
    </>
  );
}

function RetourRoute() {
  const navigate = useNavigate();
  return (
    <>
      <Seo
        title="Retour aanmelden"
        description="Meld uw retour aan bij defect, transportschade of een verkeerde levering. U ontvangt een RMA-nummer en wij regelen de afhandeling."
        canonicalPath="/retour"
      />
      <RetourPage onHome={() => navigate("/")} />
    </>
  );
}

/** Admin keeps its own minimal chrome (no public header/footer). */
function AdminRoute() {
  return (
    <div className="min-h-screen bg-primary-50">
      <Seo title="Beheer" noindex />
      <header className="border-b border-primary-100 bg-white">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-4 py-3">
          <span className="font-bold text-primary-900">Glastotaal · Beheer</span>
          <a href="/" className="text-sm font-medium text-primary-600 hover:text-primary-900">← Naar de webshop</a>
        </div>
      </header>
      <AdminPage />
    </div>
  );
}

export function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // GA4 page_view per SPA-navigatie. React Router wisselt van pagina zonder een echte paginalaad,
  // dus zonder dit ziet GA4 alleen de allereerste pagina van een bezoek — elke klik daarna was tot
  // nu toe onzichtbaar (gtag's eigen automatische page_view staat uit, zie consent.ts). /admin/*
  // slaan we bewust over: dat is intern beheerverkeer, geen bezoekersdata.
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    // Klik-ID uit de advertentie-URL vastleggen (met marketing-toestemming) zodat een bestelling
    // later aan de klik gekoppeld kan worden, ook als de klant niet op de bedankpagina terugkomt.
    captureClickId(location.search);
    trackPageView(location.pathname + location.search, document.title);
  }, [location.pathname, location.search]);

  // Backward-compat for old hash links (#admin / #herroepen → real paths).
  useEffect(() => {
    const redirect = (): void => {
      const h = window.location.hash;
      if (h === "#admin") navigate("/admin", { replace: true });
      else if (h === "#herroepen") navigate("/herroepen", { replace: true });
    };
    redirect();
    window.addEventListener("hashchange", redirect);
    return () => window.removeEventListener("hashchange", redirect);
  }, [navigate]);

  return (
    <RouteErrorBoundary>
    {/* Vertraagde fallback: eerst niets (geen flits op snelle chunks < 200 ms), na 400 ms een
        rustige spinner voor trage verbindingen. Zie RouteFallback. */}
    <Suspense fallback={<RouteFallback />}>
      <Routes>
      <Route path="/admin/*" element={<AdminRoute />} />
      {/* Insluitbare rekentool voor andermans site: bewust buiten SiteLayout, want een
          menubalk en cookiemelding horen niet in een iframe van een derde. */}
      <Route path="/embed/glasgewicht" element={<GlassWeightCalculator embed />} />
      {/* Alleen voor de build: hier schiet render-shower-presets.mjs de preset-plaatjes van. */}
      <Route path="/render/douche" element={<ShowerRenderRoute />} />
      <Route element={<SiteLayout />}>
        <Route index element={<RootIndex />} />
        <Route path="/glassoorten" element={<GlassoortenPage />} />
        <Route path="/glas-op-maat" element={<GlasOverviewRoute />} />
        {/* Bouwglas-gehard was een dubbele "Gehard glas" — samengevoegd met /glas-op-maat/gehard-glas. */}
        <Route path="/glas-op-maat/bouwglas-gehard" element={<Navigate to="/glas-op-maat/gehard-glas" replace />} />
        <Route path="/glas-op-maat/:type" element={<ConfiguratorRoute />} />
        <Route path="/glas-op-maat/:type/samenstellen" element={<SamenstellenRedirect />} />
        <Route path="/isolatieglas/samenstellen" element={<IsolatieglasConfigurator />} />
        <Route path="/triple-glas/samenstellen" element={<TripleConfigurator />} />
        <Route path="/douche/samenstellen" element={<ShowerConfigurator />} />
        <Route path="/douche/opstellingen" element={<ShowerPresetsPage />} />
        <Route path="/installatieglas/samenstellen" element={<Navigate to="/glas-op-maat/installatieglas" replace />} />
        <Route path="/categorie/isolatieglas" element={<IsolatieglasLanding />} />
        <Route path="/categorie/installatieglas" element={<InstallatieglasLanding />} />
        <Route path="/categorie/veiligheidsglas" element={<CategoryGlasLanding config={CATEGORY_LANDINGS["veiligheidsglas"]!} />} />
        <Route path="/categorie/gekleurd-glas" element={<CategoryGlasLanding config={CATEGORY_LANDINGS["gekleurd-glas"]!} />} />
        <Route path="/categorie/deuren" element={<Navigate to="/categorie/installatieglas" replace />} />
        <Route path="/categorie/:slug" element={<CategoryPage />} />
        {/* Groep en subgroep als eigen adres: /categorie/glasartikelen/gereedschappen. Zo heeft elke
            groep een vindbare pagina met eigen titel en H1, in plaats van 50 groepen achter één
            ?groep=-parameter met dezelfde canonical. */}
        <Route path="/categorie/:slug/:groep" element={<CategoryPage />} />
        <Route path="/categorie/:slug/:groep/:sub" element={<CategoryPage />} />
        <Route path="/artikel/:slug" element={<ProductPage />} />
        <Route path="/zoeken" element={<SearchResultsPage />} />
        <Route path="/u-waarde-calculator" element={<UValueCalculator />} />
        <Route path="/glasgewicht-calculator" element={<GlassWeightCalculator />} />
        <Route path="/winkelwagen" element={<CartRoute />} />
        <Route path="/afrekenen" element={<CheckoutRoute />} />
        {/* De pagina achter de betaallink in onze mails. Maakt bij elke klik een verse betaling,
            zodat die link niet kan verlopen zoals de Mollie-URL zelf (order 1098). */}
        <Route
          path="/betalen"
          element={
            <>
              <Seo title="Betalen" canonicalPath="/betalen" noindex />
              <BetaalLinkPage />
            </>
          }
        />
        <Route path="/betaling" element={<BetalingRoute />} />
        <Route path="/herroepen" element={<HerroepenRoute />} />
        <Route path="/retour" element={<RetourRoute />} />
        {/* Afmelden voor de nieuwsbrief — ook op de adressen die mensen zelf intypen. */}
        <Route path="/nieuwsbrief/afmelden" element={<AfmeldenRoute />} />
        <Route path="/afmelden" element={<AfmeldenRoute />} />
        <Route path="/uitschrijven" element={<AfmeldenRoute />} />
        <Route path="/over-ons" element={<OverOns />} />
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/veelgestelde-vragen" element={<FAQ />} />
        <Route path="/vacuumglas" element={<VacuumglasLanding />} />
        {/* Oude 'standaard'-route → eerste coating-pagina (geen aparte Standaard meer). */}
        <Route path="/vacuumglas/standaard" element={<Navigate to="/vacuumglas/single-coated" replace />} />
        {/* Extra Clear staat niet meer in de BENGglas-prijslijst — Monu is hetzelfde extra
            heldere glas (zelfde specs/prijzen), dus die pagina neemt het over. */}
        <Route path="/vacuumglas/extra-clear" element={<Navigate to="/vacuumglas/monu" replace />} />
        {BENG_VARIANTS.map((v) => (
          <Route key={v.slug} path={v.path} element={<BenglasPage variant={v} />} />
        ))}
        <Route path="/levertijden" element={<Levertijden />} />
        <Route path="/herroepingsrecht" element={<Herroepingsrecht />} />
        <Route path="/garantie" element={<Garantie />} />
        {/* Documentatie bij het publieke prijs-eindpunt; het eindpunt verwijst hiernaar. */}
        <Route path="/api-glasprijs" element={<ApiGlasprijs />} />
        <Route path="/algemene-voorwaarden" element={<AlgemeneVoorwaarden />} />
        <Route path="/privacy" element={<Privacybeleid />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/zakelijk" element={<Zakelijk />} />
        <Route path="/glas-laten-plaatsen" element={<GlasLatenPlaatsen />} />
        <Route path="/acties" element={<Acties />} />
        <Route path="/kennisbank" element={<KnowledgeIndexPage />} />
        <Route path="/kennisbank/:slug" element={<KnowledgeArticlePage />} />

        {/* Lokale SEO — glas op maat bezorgd per stad */}
        <Route path="/glas-bezorgen" element={<CityIndexPage />} />
        <Route path="/glas-bezorgen/:stad" element={<CityPage />} />

        {/* Klantaccount */}
        <Route path="/inloggen" element={<LoginPage />} />
        <Route path="/registreren" element={<RegisterPage />} />
        <Route path="/wachtwoord-vergeten" element={<ForgotPasswordPage />} />
        <Route path="/account" element={<AccountLayout />}>
          <Route index element={<AccountHome />} />
          <Route path="bestellingen" element={<AccountOrders />} />
          <Route path="bewaarde-maten" element={<AccountSaved />} />
          <Route path="adressen" element={<AccountAddresses />} />
          <Route path="gegevens" element={<AccountDetails />} />
          <Route path="voorkeuren" element={<AccountPreferences />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
      </Routes>
    </Suspense>
    </RouteErrorBoundary>
  );
}
